import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from '@/components/breathing/StepperInput';
import { Wind, Plus, Volume2, VolumeX, Home, X, Pencil, Trash2, MoreVertical, Info, ArrowLeft, StickyNote, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/utils/constants';
import { playTone, initVoices } from '@/utils/audioUtils';
import { preloadExerciseBackgrounds } from '@/utils/exerciseBackgrounds';
import ButeykoModal from '@/components/breathing/ButeykoModal';
import Technique478Modal from '@/components/breathing/Technique478Modal';
import PursedLipModal from '@/components/breathing/PursedLipModal';
import CalmingBreathModal from '@/components/breathing/CalmingBreathModal';
import RussianMastersModal from '@/components/breathing/RussianMastersModal';
import ChallengeCircle from '@/components/breathing/ChallengeCircle';
import CustomExerciseModal from '@/components/breathing/CustomExerciseModal';
import CoherenceModal from '@/components/breathing/CoherenceModal';
import BoxBreathingModal from '@/components/breathing/BoxBreathingModal';
import ChallengeModeContent from '@/components/breathing/ChallengeModeContent';
import GuidedModeContent from '@/components/breathing/GuidedModeContent';
import ReduceFrequencyModal from '@/components/breathing/ReduceFrequencyModal';
import HapticBreathingModal from '@/components/breathing/HapticBreathingModal';
import AlternateNostrilModal from '@/components/breathing/AlternateNostrilModal';
import BellowsBreathModal from '@/components/breathing/BellowsBreathModal';
import WimHofModal from '@/components/breathing/WimHofModal';
import ExtremeBreathingModal from '@/components/breathing/ExtremeBreathingModal';


const BreathingAppPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [patterns, setPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready');
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showCoherenceModal, setShowCoherenceModal] = useState(false);
  const [showBoxBreathingModal, setShowBoxBreathingModal] = useState(false);
  const [showButeykoModal, setShowButeykoModal] = useState(false);
  const [show478Modal, setShow478Modal] = useState(false);
  const [showPursedLipModal, setShowPursedLipModal] = useState(false);
  const [showCalmingModal, setShowCalmingModal] = useState(false);
  const [showRussianMastersModal, setShowRussianMastersModal] = useState(false);
  const [showReduceFrequencyModal, setShowReduceFrequencyModal] = useState(false);
  const [showHapticBreathingModal, setShowHapticBreathingModal] = useState(false);
  const [showAlternateNostrilModal, setShowAlternateNostrilModal] = useState(false);
  const [showBellowsBreathModal, setShowBellowsBreathModal] = useState(false);
  const [showWimHofModal, setShowWimHofModal] = useState(false);
  const [showExtremeModal, setShowExtremeModal] = useState(false);
  const [showCustomExerciseModal, setShowCustomExerciseModal] = useState(false);
  const [selectedCustomPattern, setSelectedCustomPattern] = useState(null);
  const [showInstructionsPopup, setShowInstructionsPopup] = useState(false);
  
  // Edit/Delete pattern states
  const [editingPattern, setEditingPattern] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [patternMenuOpen, setPatternMenuOpen] = useState(null);
  
  // Challenge mode states - MUST be declared before useEffect that uses them
  const [challengeMode, setChallengeMode] = useState(false);
  // Reflect the challenge-mode flag on <body> so global chrome (bottom Navigation
  // brand strip) can hide itself just on this page — the requirement was to
  // remove the bottom brand logo when the horizontal one is shown at the top.
  useEffect(() => {
    if (challengeMode) {
      document.body.setAttribute('data-challenge-mode', 'true');
    } else {
      document.body.removeAttribute('data-challenge-mode');
    }
    return () => document.body.removeAttribute('data-challenge-mode');
  }, [challengeMode]);
  const [myExercisesMode, setMyExercisesMode] = useState(false);
  // Default to BOLT — it's the mandatory weekly test prescribed in every
  // patient program, so it must be the primary tab when the challenges
  // page is opened without a specific `?challenge=` query param.
  const [challengeType, setChallengeType] = useState('hold');
  const [challengeActive, setChallengeActive] = useState(false);
  const [inhaleTime, setInhaleTime] = useState(0);
  const [bestInhaleTime, setBestInhaleTime] = useState(0);
  const [bestExhaleTime, setBestExhaleTime] = useState(0);
  const [bestHoldTime, setBestHoldTime] = useState(0);
  const [bestApneaTime, setBestApneaTime] = useState(0);
  const [bestMbtTime, setBestMbtTime] = useState(0);
  const [boltPreparationPhase, setBoltPreparationPhase] = useState('idle');
  const [boltPreparationProgress, setBoltPreparationProgress] = useState(0);
  const [buteykoHoldTime, setButeykoHoldTime] = useState(10);
  
  // Coherence modal states - now managed in CoherenceModal component
  // Box Breathing modal states - now managed in BoxBreathingModal component
  
  // Ref to track sound state in intervals (avoids closure issues)
  const soundEnabledRef = useRef(true);
  
  // Ref for instructions section (scroll target)
  const instructionsRef = useRef(null);
  
  // Function to scroll to instructions
  const scrollToInstructions = () => {
    instructionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  
  // Keep soundEnabledRef in sync with soundEnabled state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Preload guided-exercise background images once on mount to avoid
  // any flash/lag when a modal first opens.
  useEffect(() => {
    preloadExerciseBackgrounds();
  }, []);
  
  // Handle URL parameters for direct challenge access
  useEffect(() => {
    const challenge = searchParams.get('challenge');
    const mode = searchParams.get('mode');
    const boltOnly = searchParams.get('bolt_only') === '1';
    // "bolt_only" is a shortcut routed from the dedicated /breathing/bolt page
    // (and from Mon Programme's "Commencer BOLT" button): force BOLT and hide
    // the switcher pills so elderly patients can't get lost in sub-tabs.
    if (boltOnly) {
      setChallengeMode(true);
      setMyExercisesMode(false);
      setChallengeType('hold');
      return;
    }
    
    // Handle mode parameter from ExercisesHubPage
    if (mode === 'guided') {
      setChallengeMode(false);
      setMyExercisesMode(false);
    } else if (mode === 'challenge') {
      setChallengeMode(true);
      setMyExercisesMode(false);
    } else if (mode === 'custom') {
      setChallengeMode(false);
      setMyExercisesMode(true);
    }
    
    // Handle specific challenge types
    if (challenge === 'bolt') {
      setChallengeMode(true);
      setChallengeType('hold');
    } else if (challenge === 'mbt') {
      setChallengeMode(true);
      setChallengeType('mbt');
    } else if (challenge === 'inhale') {
      setChallengeMode(true);
      setChallengeType('inhale');
    } else if (challenge === 'exhale') {
      setChallengeMode(true);
      setChallengeType('exhale');
    }
  }, [searchParams]);

  // Auto-open a guided exercise modal based on the ?exercise=... URL param.
  // Runs when patterns become available so Buteyko/Petites Pauses lookup succeeds.
  useEffect(() => {
    const exercise = searchParams.get('exercise');
    if (!exercise) return;
    // Simple direct-open modals (no pattern lookup needed)
    const directMap = {
      'reduire-frequence': setShowReduceFrequencyModal,
      'coherence': setShowCoherenceModal,
      'box': setShowBoxBreathingModal,
      'technique-4-7-8': setShow478Modal,
      '4-7-8': setShow478Modal,
      'pursed-lip': setShowPursedLipModal,
      'calming': setShowCalmingModal,
      'russian-masters': setShowRussianMastersModal,
      'haptic': setShowHapticBreathingModal,
      'alternate-nostril': setShowAlternateNostrilModal,
      'bellows': setShowBellowsBreathModal,
      'wim-hof': setShowWimHofModal,
      'extreme': setShowExtremeModal,
    };
    if (directMap[exercise]) {
      directMap[exercise](true);
      return;
    }
    // Pattern-based modals
    if (exercise === 'petites-pauses' || exercise === 'buteyko') {
      const pattern = patterns.find(p =>
        p.name?.toLowerCase().includes('buteyko') ||
        p.name?.toLowerCase().includes('petites pauses')
      );
      if (pattern) {
        setSelectedPattern(pattern);
        setShowButeykoModal(true);
      }
    }
  }, [searchParams, patterns]);
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    // Some browsers need a slight delay
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);
  
  // Current session scores (last attempt)
  const [lastInhaleScore, setLastInhaleScore] = useState(null);
  const [lastExhaleScore, setLastExhaleScore] = useState(null);
  const [lastHoldScore, setLastHoldScore] = useState(null);
  const [lastApneaScore, setLastApneaScore] = useState(null);
  const [lastMBTScore, setLastMBTScore] = useState(null);
  
  // Ranking popup state
  const [showRankingPopup, setShowRankingPopup] = useState(false);
  const [rankingData, setRankingData] = useState(null);
  // Score just achieved in the challenge that opened the popup (not the historical best)
  const [currentChallengeScore, setCurrentChallengeScore] = useState(null);
  // ID of the challenge record just created (used to attach an optional note)
  const [currentChallengeId, setCurrentChallengeId] = useState(null);
  const [challengeNoteDraft, setChallengeNoteDraft] = useState('');
  const [challengeNoteSaving, setChallengeNoteSaving] = useState(false);
  const [challengeNoteSaved, setChallengeNoteSaved] = useState(false);
  
  // Best MBT Steps (loaded from API)
  const [bestMBTSteps, setBestMBTSteps] = useState(0);
  
  // Load challenge records from API
  useEffect(() => {
    const loadRecords = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/challenges/records`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const records = response.data;
        
        // Update best times from API records
        if (records.inhale?.duration_seconds) {
          setBestInhaleTime(records.inhale.duration_seconds);
        }
        if (records.exhale?.duration_seconds) {
          setBestExhaleTime(records.exhale.duration_seconds);
        }
        if (records.hold?.duration_seconds) {
          setBestHoldTime(records.hold.duration_seconds);
        }
        if (records.apnea?.duration_seconds) {
          setBestApneaTime(records.apnea.duration_seconds);
        }
        if (records.mbt?.steps) {
          setBestMBTSteps(records.mbt.steps);
        }
      } catch (error) {
        console.error('Failed to load challenge records:', error);
      }
    };
    
    loadRecords();
  }, []);
  
  // MBT (Maximum Breathlessness Test) state
  const [mbtSteps, setMbtSteps] = useState('');
  const [showMBTInput, setShowMBTInput] = useState(false);
  
  // Start timeout for BOLT
  const [startTimeout, setStartTimeout] = useState(null);
  
  // Quick create custom pattern
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [newPattern, setNewPattern] = useState({
    name: '',
    description: '',
    inhale_duration: 4,
    hold_after_inhale: 4,
    exhale_duration: 4,
    hold_after_exhale: 4
  });

  const fetchPatterns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patterns`);
      setPatterns(response.data);
      if (response.data.length > 0 && !selectedPattern) {
        setSelectedPattern(response.data[0]);
      }
    } catch {
      toast.error("Échec du chargement des exercices");
    }
  }, []);

  useEffect(() => {
    fetchPatterns();
  }, [fetchPatterns]);

  const handleQuickCreate = async () => {
    if (!newPattern.name.trim()) {
      toast.error("Veuillez entrer un nom pour l'exercice");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/patterns`, newPattern);
      toast.success("Exercice personnalisé créé !");
      setQuickCreateOpen(false);
      setNewPattern({
        name: '',
        description: '',
        inhale_duration: 4,
        hold_after_inhale: 4,
        exhale_duration: 4,
        hold_after_exhale: 4
      });
      fetchPatterns();
      setSelectedPattern(response.data);
    } catch {
      toast.error("Échec de la création de l'exercice");
    }
  };

  // Delete custom pattern
  const handleDeletePattern = async (patternId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet exercice ?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/patterns/${patternId}`);
      toast.success("Exercice supprimé");
      setPatternMenuOpen(null);
      fetchPatterns();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  // Update custom pattern
  const handleUpdatePattern = async () => {
    if (!editingPattern || !editingPattern.name.trim()) {
      toast.error("Veuillez entrer un nom pour l'exercice");
      return;
    }
    
    try {
      await axios.put(`${API}/patterns/${editingPattern.id}`, {
        name: editingPattern.name,
        description: editingPattern.description,
        inhale_duration: editingPattern.inhale_duration,
        hold_after_inhale: editingPattern.hold_after_inhale,
        exhale_duration: editingPattern.exhale_duration,
        hold_after_exhale: editingPattern.hold_after_exhale
      });
      toast.success("Exercice modifié !");
      setShowEditDialog(false);
      setEditingPattern(null);
      fetchPatterns();
    } catch {
      toast.error("Échec de la modification");
    }
  };

  // Breathing cycle logic
  useEffect(() => {
    if (!isActive || isPaused || !selectedPattern) return;

    // Handle custom timings for special patterns
    let inhaleDuration = selectedPattern.inhale_duration;
    let holdAfterInhale = selectedPattern.hold_after_inhale;
    let exhaleDuration = selectedPattern.exhale_duration;
    let holdAfterExhale = selectedPattern.hold_after_exhale;
    
    if (selectedPattern.id === 'buteyko-pauses') {
      holdAfterExhale = buteykoHoldTime;
    }

    const phases = [
      { name: 'inhale', duration: inhaleDuration },
      { name: 'hold-in', duration: holdAfterInhale },
      { name: 'exhale', duration: exhaleDuration },
      { name: 'hold-out', duration: holdAfterExhale },
    ].filter(p => p.duration > 0);

    let currentPhaseIndex = phases.findIndex(p => p.name === phase);
    if (currentPhaseIndex === -1) currentPhaseIndex = 0;

    const currentPhaseDuration = phases[currentPhaseIndex].duration * 1000;
    const updateInterval = 50;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += updateInterval;
      const newProgress = (elapsed / currentPhaseDuration) * 100;

      if (newProgress >= 100) {
        const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        const nextPhaseName = phases[nextPhaseIndex].name;
        setPhase(nextPhaseName);
        setProgress(0);

        if (nextPhaseIndex === 0) {
          setCyclesCompleted(prev => prev + 1);
        }
      } else {
        setProgress(newProgress);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isActive, isPaused, phase, selectedPattern, soundEnabled, buteykoHoldTime]);

  // Timer
  useEffect(() => {
    if (!isActive || isPaused) return;
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isActive, isPaused]);

  // BOLT preparation sequence
  useEffect(() => {
    if (!challengeActive || challengeType !== 'hold' || !boltPreparationPhase) return;
    
    const phaseDuration = boltPreparationPhase === 'inhale' ? 2000 : boltPreparationPhase === 'exhale' ? 3000 : boltPreparationPhase === 'pause' ? 1000 : 0;
    if (phaseDuration === 0) return;
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / phaseDuration, 1);
      setBoltPreparationProgress(progress);
      
      if (progress >= 1) {
        if (boltPreparationPhase === 'inhale') {
          // Move to exhale phase
          setBoltPreparationPhase('exhale');
          setBoltPreparationProgress(0);
          if (soundEnabledRef.current) playTone(330, 0.3);
        } else if (boltPreparationPhase === 'exhale') {
          // Move to pause phase (1 second wait)
          setBoltPreparationPhase('pause');
          setBoltPreparationProgress(0);
        } else if (boltPreparationPhase === 'pause') {
          // After pause, move to apnee phase and start timer
          setBoltPreparationPhase('apnee');
          setBoltPreparationProgress(0);
          if (soundEnabledRef.current) playTone(380, 0.3);
        }
      }
    }, 50);
    
    return () => clearInterval(interval);
  }, [challengeActive, challengeType, boltPreparationPhase]);

  // Max Inhale Challenge Timer
  useEffect(() => {
    if (!challengeActive) return;
    
    // For BOLT, only count time during apnea phase
    if (challengeType === 'hold' && boltPreparationPhase !== 'apnee') return;
    
    const interval = setInterval(() => {
      setInhaleTime(prev => prev + 0.1);
    }, 100);
    return () => clearInterval(interval);
  }, [challengeActive, challengeType, boltPreparationPhase]);

  // Callback for changing challenge type
  const handleChallengeTypeChange = (newType) => {
    setChallengeType(newType);
    setChallengeActive(false);
    setInhaleTime(0);
    if (newType !== 'mbt') {
      setBoltPreparationPhase(null);
      setBoltPreparationProgress(0);
    }
    if (newType === 'mbt') {
      setShowMBTInput(false);
    }
  };

  // Callback to go back to guided mode
  const handleBackToGuided = () => {
    setChallengeMode(false);
    setChallengeActive(false);
    setInhaleTime(0);
  };

  const startChallenge = () => {
    // Prevent multiple starts
    if (challengeActive) return;
    
    // For BOLT, start with preparation sequence
    if (challengeType === 'hold') {
      setBoltPreparationPhase('inhale');
      setBoltPreparationProgress(0);
      setChallengeActive(true);
      setInhaleTime(0);
      if (soundEnabled) playTone(440, 0.3);
      return;
    }
    
    // For inhale/exhale challenges, start immediately
    setChallengeActive(true);
    setInhaleTime(0);
    if (soundEnabled) {
      if (challengeType === 'inhale') playTone(440, 0.3);
      else if (challengeType === 'exhale') playTone(330, 0.3);
    }
  };

  const stopChallenge = async () => {
    // Only stop if challenge is actually active
    if (!challengeActive) return;
    
    setChallengeActive(false);
    setBoltPreparationPhase(null);
    setBoltPreparationProgress(0);
    
    if (soundEnabled) {
      if (challengeType === 'inhale') playTone(330, 0.3);
      else if (challengeType === 'exhale') playTone(440, 0.3);
      else if (challengeType === 'apnea') playTone(380, 0.3);
      else playTone(380, 0.3);
    }
    
    if (inhaleTime < 1) {
      setInhaleTime(0);
      return;
    }
    
    let bestTime;
    if (challengeType === 'inhale') {
      bestTime = bestInhaleTime;
    } else if (challengeType === 'exhale') {
      bestTime = bestExhaleTime;
    } else if (challengeType === 'apnea') {
      bestTime = bestApneaTime;
    } else {
      bestTime = bestHoldTime;
    }
    
    const isNewRecord = inhaleTime > bestTime;
    
    try {
      const resp = await axios.post(`${API}/challenges`, {
        challenge_type: challengeType,
        duration_seconds: inhaleTime,
        is_record: isNewRecord
      });
      setCurrentChallengeId(resp?.data?.id || null);
      setChallengeNoteDraft('');
      setChallengeNoteSaved(false);
    } catch (error) {
      console.error("Failed to save challenge score", error);
      setCurrentChallengeId(null);
    }
    
    // Save last session score
    if (challengeType === 'inhale') {
      setLastInhaleScore(inhaleTime);
    } else if (challengeType === 'exhale') {
      setLastExhaleScore(inhaleTime);
    } else if (challengeType === 'apnea') {
      setLastApneaScore(inhaleTime);
    } else {
      setLastHoldScore(inhaleTime);
    }
    
    if (isNewRecord) {
      if (challengeType === 'inhale') {
        setBestInhaleTime(inhaleTime);
      } else if (challengeType === 'exhale') {
        setBestExhaleTime(inhaleTime);
      } else if (challengeType === 'apnea') {
        setBestApneaTime(inhaleTime);
      } else {
        setBestHoldTime(inhaleTime);
      }
      toast.success(`Nouveau record ! ${inhaleTime.toFixed(1)} secondes !`);
    } else {
      toast.info(`${inhaleTime.toFixed(1)} secondes. Record : ${bestTime.toFixed(1)}s`);
    }

    // BOLT ("hold") progression toast — celebrate when the just-saved score
    // hits the next unlock threshold in the 30-day window.
    if (challengeType === 'hold') {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/challenges?challenge_type=bolt&limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(r.data) ? r.data : (r.data.challenges || []);
        const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
        const recent = items
          .map((it) => ({ v: Number(it.duration_seconds) || 0, ts: new Date(it.completed_at || it.created_at).getTime() }))
          .filter((it) => it.v > 0 && !isNaN(it.ts) && it.ts >= cutoff);
        const THRESHOLDS = [15, 20, 30];
        const NEED = 7;
        let unlockedIdx = 0;
        for (let i = 0; i < THRESHOLDS.length; i++) {
          const c = recent.filter((h) => h.v >= THRESHOLDS[i]).length;
          if (c >= NEED) unlockedIdx = i + 1;
          else break;
        }
        const nextThreshold = THRESHOLDS[unlockedIdx];
        if (nextThreshold != null && inhaleTime >= nextThreshold) {
          const countAtThreshold = recent.filter((h) => h.v >= nextThreshold).length;
          if (countAtThreshold >= NEED) {
            toast.success(`🎉 Palier ${nextThreshold} s atteint ! Le protocole Oxygen Advantage passe au niveau supérieur.`, { duration: 6000 });
          } else {
            toast.success(`Bravo ! Encore ${NEED - countAtThreshold} mesure(s) ≥ ${nextThreshold} s pour passer au palier suivant.`, { duration: 5000 });
          }
        }
      } catch { /* non-blocking */ }
    }

    // Fetch and show ranking (popup opens even if ranking call fails)
    setCurrentChallengeScore({ value: inhaleTime, unit: 'seconds', isRecord: isNewRecord });
    try {
      const token = localStorage.getItem('token');
      const rankingResponse = await axios.get(`${API}/challenges/ranking/${challengeType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRankingData(rankingResponse.data);
    } catch (error) {
      console.error('Failed to fetch ranking:', error);
      setRankingData({ ranking: null, total_participants: 0, personal_best: null, challenge_type: challengeType, message: 'Score enregistré.' });
    }
    setShowRankingPopup(true);
    
    setInhaleTime(0);
  };

  const handleMBTSubmit = async () => {
    const steps = parseInt(mbtSteps);
    if (isNaN(steps) || steps <= 0) {
      toast.error('Veuillez entrer un nombre de pas valide');
      return;
    }

    const isNewRecord = steps > bestMBTSteps;

    try {
      const resp = await axios.post(`${API}/challenges`, {
        challenge_type: 'mbt',
        steps: steps,
        is_record: isNewRecord
      });
      setCurrentChallengeId(resp?.data?.id || null);
      setChallengeNoteDraft('');
      setChallengeNoteSaved(false);
    } catch (error) {
      console.error("Failed to save MBT score", error);
      setCurrentChallengeId(null);
    }

    // Save last session score
    setLastMBTScore(steps);
    
    if (isNewRecord) {
      setBestMBTSteps(steps);
      toast.success(`Nouveau record MBT ! ${steps} pas !`);
    } else {
      toast.info(`${steps} pas enregistrés. Record : ${bestMBTSteps} pas`);
    }
    
    // Fetch and show ranking (popup opens even if ranking call fails)
    setCurrentChallengeScore({ value: steps, unit: 'steps', isRecord: isNewRecord });
    try {
      const token = localStorage.getItem('token');
      const rankingResponse = await axios.get(`${API}/challenges/ranking/mbt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRankingData(rankingResponse.data);
    } catch (error) {
      console.error('Failed to fetch ranking:', error);
      setRankingData({ ranking: null, total_participants: 0, personal_best: null, challenge_type: 'mbt', message: 'Score enregistré.' });
    }
    setShowRankingPopup(true);

    setMbtSteps('');
    setShowMBTInput(false);
  };

  const startSession = () => {
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    // Breath sounds removed for a more peaceful experience
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const stopSession = async () => {
    if (elapsedTime > 10 && selectedPattern) {
      try {
        await axios.post(`${API}/sessions`, {
          pattern_id: selectedPattern.id,
          pattern_name: selectedPattern.name,
          duration_minutes: elapsedTime / 60,
          cycles_completed: cyclesCompleted
        });
        toast.success(`Session enregistrée ! ${cyclesCompleted} cycles complétés.`);
      } catch (error) {
        console.error("Failed to save session:", error.response?.data || error.message);
        toast.error("Erreur lors de l'enregistrement de la session. Veuillez réessayer.");
      }
    } else if (elapsedTime <= 10) {
      toast.info("Session trop courte pour être enregistrée (minimum 10 secondes)");
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
  };

  // Generic function to save exercise session from any modal
  const saveExerciseSession = async (sessionData) => {
    const { patternId, patternName, durationSeconds, cyclesCompleted, retentionScores, bestRetention, settings } = sessionData;
    
    // Only save sessions longer than 10 seconds
    if (durationSeconds < 10) {
      return;
    }
    
    try {
      const payload = {
        pattern_id: patternId,
        pattern_name: patternName,
        duration_minutes: durationSeconds / 60,
        cycles_completed: cyclesCompleted || 0
      };
      
      // Add Wim Hof retention scores if available
      if (retentionScores && retentionScores.length > 0) {
        payload.retention_scores = retentionScores;
        payload.best_retention = bestRetention || Math.max(...retentionScores);
      }

      // Persist exercise-specific settings so they can be pre-filled next time
      if (settings && typeof settings === 'object') {
        payload.settings = settings;
      }
      
      const response = await axios.post(`${API}/sessions`, payload);
      
      // Custom message for Wim Hof with retention info
      if (retentionScores && retentionScores.length > 0) {
        const formatRetention = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
        toast.success(`Session Wim Hof enregistrée ! Meilleure apnée: ${formatRetention(bestRetention || Math.max(...retentionScores))}`);
      } else {
        toast.success(`Session enregistrée ! ${cyclesCompleted || 0} cycles complétés.`);
      }
      return response.data;  // returns saved session (with id) so modals can attach a rating later
    } catch (error) {
      console.error("Failed to save session:", error.response?.data || error.message);
      toast.error("Erreur lors de l'enregistrement de la session.");
      return null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="min-h-screen pb-40 sm:pb-24 relative overflow-hidden" 
      data-testid="home-page"
      style={{
        backgroundColor: '#0F2044',
        backgroundImage: "url('https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/hrao4qok_ChatGPT%20Image%2027%20ao%C3%BBt%202026%2C%2008_49_20.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Very subtle white veil on top of the aquatic photo for softness only */}
      <div className="absolute inset-0 bg-white/10 z-[2] pointer-events-none" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 pt-4 sm:pt-8">
        {/* Header */}
        <div className="relative text-center mb-4 sm:mb-8">
          {/* Bouton retour — priorité: 1) retour au protocole OA si arrivé depuis OA, 2) retour écran précédent en mode défi, 3) accueil du site */}
          {searchParams.get('exercise') ? (
            <button
              onClick={() => navigate('/breathing/oxygen-advantage')}
              className="absolute left-0 top-0 z-50 flex items-center gap-1 sm:gap-2 px-2 sm:px-5 py-1.5 sm:py-2.5 text-sm sm:text-base font-medium text-slate-800 bg-white/70 hover:bg-white/85 backdrop-blur-sm rounded-lg sm:rounded-xl transition-all shadow-sm border border-white/70 cursor-pointer"
              data-testid="back-to-oa-button"
              type="button"
            >
              <ArrowLeft size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Retour au protocole</span>
            </button>
          ) : challengeMode ? (
            <button
              onClick={() => navigate(-1)}
              className="absolute left-0 top-0 z-50 flex items-center gap-1 sm:gap-2 px-2 sm:px-5 py-1.5 sm:py-2.5 text-sm sm:text-base font-medium text-slate-800 bg-white/70 hover:bg-white/85 backdrop-blur-sm rounded-lg sm:rounded-xl transition-all shadow-sm border border-white/70 cursor-pointer"
              data-testid="challenge-back-button"
              type="button"
            >
              <ArrowLeft size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Retour</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="absolute left-0 top-0 z-50 flex items-center gap-1 sm:gap-2 px-2 sm:px-5 py-1.5 sm:py-2.5 text-sm sm:text-base font-medium text-slate-800 bg-white/70 hover:bg-white/85 backdrop-blur-sm rounded-lg sm:rounded-xl transition-all shadow-sm border border-white/70 cursor-pointer"
              data-testid="home-button"
              type="button"
            >
              <Home size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Accueil du site</span>
            </button>
          )}
          
          {/* Bouton arrêter l'exercice - visible uniquement pendant l'exercice */}
          {isActive && !challengeMode && (
            <button
              onClick={stopSession}
              className="absolute right-0 top-0 flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="stop-exercise-button"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden sm:inline">Arrêter l&apos;exercice</span>
            </button>
          )}
          
          <h1 className="text-xl sm:text-3xl font-bold text-slate-900 tracking-tight pt-10 sm:pt-0 flex items-center justify-center gap-3 sm:gap-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {/* Desktop: brand logo on the left of the "Respiration Santé" title.
                Shown on every mode (guided, challenges, OA, custom, my exercises). */}
            <img
              src="/logo-mains-nuages.png"
              alt="Physiothérapie Les Mains Nuages"
              className="hidden sm:block h-16 lg:h-20 w-auto flex-shrink-0"
              style={{ filter: 'drop-shadow(0 2px 6px rgba(15,32,64,0.25))' }}
            />
            <span>Respiration Santé</span>
          </h1>
          {/* Mobile: horizontal brand logo centered above the greeting on every
              mode so patients always see the clinic's branding. */}
          <div className="sm:hidden flex justify-center mt-3 mb-2" data-testid="breathing-mobile-brand-logo">
            <img
              src="/logo-mains-nuages.png"
              alt="Physiothérapie Les Mains Nuages"
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 2px 6px rgba(15,32,64,0.25))' }}
            />
          </div>
          {/* Desktop: separate lines, Mobile in challenge mode: single line */}
          {!challengeMode ? (
            <>
              <p className="text-slate-700 text-xs sm:text-sm mt-1">
                {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <p className="text-slate-800 text-sm sm:text-base mt-1 sm:mt-2">Bonjour, {user?.name || 'Utilisateur'}</p>
            </>
          ) : (
            <p className="text-slate-800 text-xs sm:text-sm mt-1">
              <span className="hidden sm:inline">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} • </span>
              Bonjour, {user?.name || 'Utilisateur'}
            </p>
          )}
        </div>

        {/* Pattern Selector - Only in Guided Mode (not in My Exercises or Challenge mode) */}
        {!challengeMode && !myExercisesMode && !isActive && (
          <GuidedModeContent
            patterns={patterns}
            setSelectedPattern={setSelectedPattern}
            setShowButeykoModal={setShowButeykoModal}
            setShowBoxBreathingModal={setShowBoxBreathingModal}
            setShowCoherenceModal={setShowCoherenceModal}
            setShow478Modal={setShow478Modal}
            setShowPursedLipModal={setShowPursedLipModal}
            setShowCalmingModal={setShowCalmingModal}
            setShowRussianMastersModal={setShowRussianMastersModal}
            setShowReduceFrequencyModal={setShowReduceFrequencyModal}
            setShowHapticBreathingModal={setShowHapticBreathingModal}
            setShowAlternateNostrilModal={setShowAlternateNostrilModal}
            setShowBellowsBreathModal={setShowBellowsBreathModal}
            setShowWimHofModal={setShowWimHofModal}
            setShowExtremeModal={setShowExtremeModal}
            setChallengeMode={setChallengeMode}
          />
        )}

        {/* My Exercises Mode - Shows only custom patterns */}
        {myExercisesMode && !isActive && (
          <div className="mb-4 sm:mb-8">
            {/* Create Custom Exercise Button */}
            <div className="flex justify-center mb-8">
              <Dialog open={quickCreateOpen} onOpenChange={setQuickCreateOpen}>
                <DialogTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                    data-testid="quick-create-pattern-btn"
                  >
                    <Plus size={20} />
                    Créer un exercice personnalisé
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-sm mx-auto">
                  <DialogHeader>
                    <DialogTitle>Créer un exercice personnalisé</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Nom</Label>
                      <Input
                        value={newPattern.name}
                        onChange={(e) => setNewPattern({ ...newPattern, name: e.target.value })}
                        placeholder="Mon exercice de respiration"
                        className="mt-1"
                        data-testid="quick-pattern-name"
                      />
                    </div>
                    <div>
                      <Label>Description (optionnel)</Label>
                      <Input
                        value={newPattern.description}
                        onChange={(e) => setNewPattern({ ...newPattern, description: e.target.value })}
                        placeholder="Décrivez votre exercice"
                        className="mt-1"
                        data-testid="quick-pattern-desc"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Inspiration</Label>
                        <div className="mt-2">
                          <StepperInput
                            value={newPattern.inhale_duration}
                            onChange={(v) => setNewPattern({ ...newPattern, inhale_duration: v })}
                            min={1} max={10} step={1} unit="sec" label="inspiration" colorClass="sky"
                            valueTestId="quick-inhale-value" incTestId="quick-inhale-inc" decTestId="quick-inhale-dec"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Rétention (pleine)</Label>
                        <div className="mt-2">
                          <StepperInput
                            value={newPattern.hold_after_inhale}
                            onChange={(v) => setNewPattern({ ...newPattern, hold_after_inhale: v })}
                            min={0} max={10} step={1} unit="sec" label="rétention pleine" colorClass="amber"
                            valueTestId="quick-hold-in-value" incTestId="quick-hold-in-inc" decTestId="quick-hold-in-dec"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Expiration</Label>
                        <div className="mt-2">
                          <StepperInput
                            value={newPattern.exhale_duration}
                            onChange={(v) => setNewPattern({ ...newPattern, exhale_duration: v })}
                            min={1} max={10} step={1} unit="sec" label="expiration" colorClass="rose"
                            valueTestId="quick-exhale-value" incTestId="quick-exhale-inc" decTestId="quick-exhale-dec"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Rétention (vide)</Label>
                        <div className="mt-2">
                          <StepperInput
                            value={newPattern.hold_after_exhale}
                            onChange={(v) => setNewPattern({ ...newPattern, hold_after_exhale: v })}
                            min={0} max={10} step={1} unit="sec" label="rétention vide" colorClass="violet"
                            valueTestId="quick-hold-out-value" incTestId="quick-hold-out-inc" decTestId="quick-hold-out-dec"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">Aperçu du cycle :</p>
                      <p className="text-sm text-slate-700">
                        Inspirer {newPattern.inhale_duration}s
                        {newPattern.hold_after_inhale > 0 && ` → Retenir ${newPattern.hold_after_inhale}s`}
                        {` → Expirer ${newPattern.exhale_duration}s`}
                        {newPattern.hold_after_exhale > 0 && ` → Retenir ${newPattern.hold_after_exhale}s`}
                      </p>
                    </div>
                    
                    <Button onClick={handleQuickCreate} className="w-full bg-violet-500 hover:bg-violet-600" data-testid="quick-save-btn">
                      Créer et commencer
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {patterns.filter(p => !p.is_preset).length > 0 ? (
              <>
                <Label className="text-base sm:text-lg font-semibold text-white mb-3 block px-3 py-2 bg-black/40 backdrop-blur-sm rounded-lg inline-block" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                  ✨ Mes exercices personnalisés
                </Label>
                <p className="text-white/80 text-sm mb-4 px-1" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                  Vos exercices de respiration sur mesure
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-16">
                  {patterns.filter(p => !p.is_preset).map((pattern) => (
                    <div
                      key={pattern.id}
                      className={`relative rounded-2xl border transition-all duration-300 backdrop-blur-md ${
                        selectedPattern?.id === pattern.id
                          ? "border-white/50 bg-white/40 text-white shadow-xl"
                          : "border-white/30 bg-white/20 text-white hover:bg-white/35"
                      }`}
                      style={{
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        overflow: 'visible'
                      }}
                    >
                      {/* Main button to launch exercise */}
                      <button
                        onClick={() => {
                          setSelectedCustomPattern(pattern);
                          setShowCustomExerciseModal(true);
                        }}
                        className="w-full px-4 py-5 pr-10 text-left"
                        data-testid={`my-exercise-btn-${pattern.id}`}
                      >
                        <span className="font-bold text-base drop-shadow-md">{pattern.name}</span>
                        <p className="text-white/70 text-xs mt-1">
                          {pattern.inhale_duration}s - {pattern.hold_after_inhale}s - {pattern.exhale_duration}s - {pattern.hold_after_exhale}s
                        </p>
                      </button>
                      
                      {/* Menu button */}
                      <div className="absolute top-2 right-2" style={{ overflow: 'visible' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPatternMenuOpen(patternMenuOpen === pattern.id ? null : pattern.id);
                          }}
                          className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                        >
                          <MoreVertical size={16} className="text-white/70" />
                        </button>
                        
                        {/* Dropdown menu */}
                        {patternMenuOpen === pattern.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-[9998]"
                              onClick={() => setPatternMenuOpen(null)}
                            />
                            <div 
                              className="absolute right-0 bottom-full mb-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[150px]"
                              style={{ zIndex: 9999 }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPattern({...pattern});
                                  setShowEditDialog(true);
                                  setPatternMenuOpen(null);
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Pencil size={14} />
                                Modifier
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePattern(pattern.id);
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <Trash2 size={14} />
                                Supprimer
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md mx-auto border border-white/20">
                  <p className="text-white text-lg mb-2">Aucun exercice personnalisé</p>
                  <p className="text-white/70 text-sm mb-4">Créez votre premier exercice de respiration sur mesure !</p>
                </div>
              </div>
            )}
          </div>
        )}

                {/* Challenge Mode Content */}
        {challengeMode && (
          <ChallengeModeContent
            challengeType={challengeType}
            setChallengeType={setChallengeType}
            challengeActive={challengeActive}
            inhaleTime={inhaleTime}
            boltPreparationPhase={boltPreparationPhase}
            boltPreparationProgress={boltPreparationProgress}
            showMBTInput={showMBTInput}
            setShowMBTInput={setShowMBTInput}
            mbtSteps={mbtSteps}
            setMbtSteps={setMbtSteps}
            bestInhaleTime={bestInhaleTime}
            bestExhaleTime={bestExhaleTime}
            bestHoldTime={bestHoldTime}
            bestApneaTime={bestApneaTime}
            bestMBTSteps={bestMBTSteps}
            lastInhaleScore={lastInhaleScore}
            lastExhaleScore={lastExhaleScore}
            lastHoldScore={lastHoldScore}
            lastApneaScore={lastApneaScore}
            lastMBTScore={lastMBTScore}
            soundEnabled={soundEnabled}
            setSoundEnabled={setSoundEnabled}
            showInstructionsPopup={showInstructionsPopup}
            setShowInstructionsPopup={setShowInstructionsPopup}
            onChallengeTypeChange={handleChallengeTypeChange}
            onBackToGuided={handleBackToGuided}
            boltOnly={searchParams.get('bolt_only') === '1'}
            startChallenge={startChallenge}
            stopChallenge={stopChallenge}
            handleMBTSubmit={handleMBTSubmit}
            scrollToInstructions={scrollToInstructions}
            instructionsRef={instructionsRef}
          />
        )}
      </div>

      {/* Coherence Cardiaque Modal */}
      <CoherenceModal 
        isOpen={showCoherenceModal} 
        onClose={() => setShowCoherenceModal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Box Breathing Modal */}
      <BoxBreathingModal 
        isOpen={showBoxBreathingModal} 
        onClose={() => setShowBoxBreathingModal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Buteyko Modal */}
      <ButeykoModal 
        isOpen={showButeykoModal} 
        onClose={() => setShowButeykoModal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Technique 4-7-8 Modal */}
      <Technique478Modal 
        isOpen={show478Modal} 
        onClose={() => setShow478Modal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Pursed Lip Modal */}
      <PursedLipModal 
        isOpen={showPursedLipModal} 
        onClose={() => setShowPursedLipModal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Calming Breath Modal */}
      <CalmingBreathModal 
        isOpen={showCalmingModal} 
        onClose={() => setShowCalmingModal(false)} 
        pattern={selectedPattern}
        onSessionComplete={saveExerciseSession}
      />

      {/* Russian Masters Modal */}
      <RussianMastersModal 
        isOpen={showRussianMastersModal} 
        onClose={() => setShowRussianMastersModal(false)} 
        onSessionComplete={saveExerciseSession}
      />

      {/* Reduce Frequency Modal */}
      <ReduceFrequencyModal 
        isOpen={showReduceFrequencyModal} 
        onClose={() => setShowReduceFrequencyModal(false)} 
        onSessionComplete={saveExerciseSession}
      />

      {/* Haptic Breathing Modal */}
      <HapticBreathingModal 
        isOpen={showHapticBreathingModal} 
        onClose={() => setShowHapticBreathingModal(false)} 
      />

      {/* Alternate Nostril Modal */}
      <AlternateNostrilModal 
        isOpen={showAlternateNostrilModal} 
        onClose={() => setShowAlternateNostrilModal(false)}
        onSessionComplete={saveExerciseSession}
      />

      {/* Bellows Breath Modal */}
      <BellowsBreathModal 
        isOpen={showBellowsBreathModal} 
        onClose={() => setShowBellowsBreathModal(false)}
        onSessionComplete={saveExerciseSession}
      />

      {/* Wim Hof Modal */}
      <WimHofModal 
        isOpen={showWimHofModal} 
        onClose={() => setShowWimHofModal(false)}
        onSessionComplete={saveExerciseSession}
      />

      {/* Extreme Breathing Modal */}
      <ExtremeBreathingModal 
        isOpen={showExtremeModal} 
        onClose={() => setShowExtremeModal(false)}
        onComplete={(data) => {
          saveExerciseSession({
            patternName: 'Respiration Extrême',
            duration: data.totalTime,
            cycles: data.cycles
          });
        }}
      />

      {/* Custom Exercise Modal */}
      {/* Edit Pattern Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle>Modifier l&apos;exercice</DialogTitle>
          </DialogHeader>
          {editingPattern && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Nom</Label>
                <Input
                  value={editingPattern.name}
                  onChange={(e) => setEditingPattern({ ...editingPattern, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description (optionnel)</Label>
                <Input
                  value={editingPattern.description || ''}
                  onChange={(e) => setEditingPattern({ ...editingPattern, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Inspiration</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={editingPattern.inhale_duration}
                      onChange={(v) => setEditingPattern({ ...editingPattern, inhale_duration: v })}
                      min={1} max={10} step={1} unit="sec" label="inspiration" colorClass="sky"
                      valueTestId="edit-inhale-value" incTestId="edit-inhale-inc" decTestId="edit-inhale-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Rétention (inspir)</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={editingPattern.hold_after_inhale}
                      onChange={(v) => setEditingPattern({ ...editingPattern, hold_after_inhale: v })}
                      min={0} max={10} step={1} unit="sec" label="rétention pleine" colorClass="amber"
                      valueTestId="edit-hold-in-value" incTestId="edit-hold-in-inc" decTestId="edit-hold-in-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Expiration</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={editingPattern.exhale_duration}
                      onChange={(v) => setEditingPattern({ ...editingPattern, exhale_duration: v })}
                      min={1} max={10} step={1} unit="sec" label="expiration" colorClass="rose"
                      valueTestId="edit-exhale-value" incTestId="edit-exhale-inc" decTestId="edit-exhale-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Rétention (expir)</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={editingPattern.hold_after_exhale}
                      onChange={(v) => setEditingPattern({ ...editingPattern, hold_after_exhale: v })}
                      min={0} max={10} step={1} unit="sec" label="rétention vide" colorClass="violet"
                      valueTestId="edit-hold-out-value" incTestId="edit-hold-out-inc" decTestId="edit-hold-out-dec"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingPattern(null);
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button onClick={handleUpdatePattern} className="flex-1">
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Instructions Popup */}
      {showInstructionsPopup && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowInstructionsPopup(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Popup Content */}
          <div 
            className="relative w-full max-w-sm p-5 rounded-2xl border border-white/20"
            style={{
              background: challengeType === 'inhale' 
                ? 'linear-gradient(135deg, rgba(251,191,36,0.9) 0%, rgba(180,83,9,0.95) 100%)'
                : challengeType === 'exhale'
                ? 'linear-gradient(135deg, rgba(56,189,248,0.9) 0%, rgba(3,105,161,0.95) 100%)'
                : challengeType === 'mbt'
                ? 'linear-gradient(135deg, rgba(251,113,133,0.9) 0%, rgba(190,18,60,0.95) 100%)'
                : 'linear-gradient(135deg, rgba(167,139,250,0.9) 0%, rgba(91,33,182,0.95) 100%)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowInstructionsPopup(false)}
              className="absolute top-3 right-3 p-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
            
            {/* Title */}
            <h3 className="text-white font-bold text-lg mb-4 pr-8">
              Instructions {challengeType === 'inhale' ? 'Inspiration' : challengeType === 'exhale' ? 'Expiration' : challengeType === 'apnea' ? 'Apnée inspiratoire' : challengeType === 'mbt' ? 'MBT' : 'BOLT'}
            </h3>
            
            {/* Instructions content */}
            <ol className="text-sm space-y-2.5">
              {challengeType === 'inhale' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Expirez tout l&apos;air de vos poumons</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et inspirez le plus doucement possible</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont remplis, lâchez le bouton</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'exhale' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez jusqu&apos;à remplir vos poumons au maximum</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et expirez le plus doucement possible</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Lorsque vos poumons sont vides, lâchez le bouton</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Observez votre record du jour</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'apnea' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Inspirez profondément pour remplir complètement vos poumons</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Appuyez sur le bouton et retenez votre souffle</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Tenez le plus longtemps possible sans forcer</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Quand vous ne pouvez plus tenir, lâchez le bouton</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Essayez de battre votre record !</li>
                </>
              ) : challengeType === 'hold' ? (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Appuyez sur le bouton pour démarrer</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Suivez les instructions : Inspirez → Expirez</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Retenez votre souffle après l&apos;expiration</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Arrêtez au premier besoin d&apos;air</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Normal : 25s / Athlète : 40s</li>
                </>
              ) : (
                <>
                  <li className="text-white/90"><span className="font-bold text-white">1.</span> Expirez normalement</li>
                  <li className="text-white/90"><span className="font-bold text-white">2.</span> Pincez votre nez</li>
                  <li className="text-white/90"><span className="font-bold text-white">3.</span> Marchez à un rythme normal</li>
                  <li className="text-white/90"><span className="font-bold text-white">4.</span> Comptez vos pas</li>
                  <li className="text-white/90"><span className="font-bold text-white">5.</span> Arrêtez au premier besoin d&apos;air</li>
                </>
              )}
            </ol>
          </div>
        </div>
      )}

      <CustomExerciseModal 
        isOpen={showCustomExerciseModal} 
        onClose={() => setShowCustomExerciseModal(false)} 
        pattern={selectedCustomPattern}
      />

      {/* Ranking Popup */}
      {showRankingPopup && rankingData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div 
            className="relative max-w-md w-full p-6 rounded-3xl text-center max-h-[90vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => { setShowRankingPopup(false); setCurrentChallengeScore(null); setCurrentChallengeId(null); setChallengeNoteDraft(''); setChallengeNoteSaved(false); }}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
              data-testid="ranking-popup-close"
            >
              <X size={24} />
            </button>
            
            {/* Trophy icon for top 3 */}
            {rankingData.ranking && rankingData.ranking <= 3 && (
              <div className="mb-4">
                <span className="text-6xl">
                  {rankingData.ranking === 1 ? '🏆' : rankingData.ranking === 2 ? '🥈' : '🥉'}
                </span>
              </div>
            )}
            
            {/* Ranking number */}
            {rankingData.ranking && (
              <div className="mb-4">
                <span 
                  className="text-6xl font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  #{rankingData.ranking}
                </span>
                <p className="text-white/70 text-sm mt-1">
                  sur {rankingData.total_participants} participant{rankingData.total_participants > 1 ? 's' : ''}
                </p>
              </div>
            )}
            
            {/* Score achieved in this session (NOT the historical record) */}
            {currentChallengeScore && (
              <div className="mb-4 p-4 rounded-xl bg-white/15 border border-white/25">
                <p className="text-white/70 text-xs uppercase tracking-wide mb-1">Score de cette session</p>
                <p className="text-3xl font-bold text-white" data-testid="ranking-popup-current-score">
                  {currentChallengeScore.unit === 'steps'
                    ? `${currentChallengeScore.value} pas`
                    : `${currentChallengeScore.value.toFixed(1)} secondes`}
                </p>
                {currentChallengeScore.isRecord && (
                  <p className="text-xs mt-1 font-semibold" style={{ color: '#FBBF24' }}>
                    Nouveau record personnel !
                  </p>
                )}
              </div>
            )}

            {/* Personal best (historical) */}
            {rankingData.personal_best && (
              <div className="mb-4 p-3 rounded-xl bg-white/10">
                <p className="text-white/60 text-xs uppercase tracking-wide mb-1">Votre record personnel</p>
                <p className="text-2xl font-bold text-white" data-testid="ranking-popup-personal-best">
                  {rankingData.challenge_type === 'mbt' 
                    ? `${rankingData.personal_best} pas` 
                    : `${rankingData.personal_best.toFixed(1)} secondes`}
                </p>
              </div>
            )}
            
            {/* Motivational message */}
            <p className="text-white text-lg mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {rankingData.message}
            </p>

            {/* BOLT interpretation legend (hold challenge) or MBT interpretation (mbt) */}
            {(rankingData.challenge_type === 'hold' || rankingData.challenge_type === 'mbt') && currentChallengeScore && (() => {
              const isBolt = rankingData.challenge_type === 'hold';
              const value = Number(currentChallengeScore.value) || 0;
              const rows = isBolt
                ? [
                    { min: 0, max: 10, status: 'Très faible', tone: 'rose', hint: 'Hyperventilation sévère probable' },
                    { min: 10, max: 20, status: 'Faible', tone: 'orange', hint: 'Hyperventilation modérée' },
                    { min: 20, max: 30, status: 'Moyen', tone: 'amber', hint: 'Tolérance acceptable — perfectible' },
                    { min: 30, max: 40, status: 'Bon', tone: 'lime', hint: 'Bonne tolérance au CO₂' },
                    { min: 40, max: Infinity, status: 'Excellent', tone: 'emerald', hint: 'Santé respiratoire optimale' },
                  ]
                : [
                    { min: 0, max: 30, status: 'Très faible', tone: 'rose', hint: 'Tolérance très limitée' },
                    { min: 30, max: 50, status: 'Faible', tone: 'orange', hint: 'Sous la normale' },
                    { min: 50, max: 70, status: 'Moyen', tone: 'amber', hint: 'En progression' },
                    { min: 70, max: 100, status: 'Bon', tone: 'lime', hint: 'Bonne tolérance fonctionnelle' },
                    { min: 100, max: Infinity, status: 'Excellent', tone: 'emerald', hint: 'Niveau athlétique' },
                  ];
              const tonePalette = {
                rose:    { bg: 'bg-rose-500/20', border: 'border-rose-400',    text: 'text-rose-100',    dot: 'bg-rose-400' },
                orange:  { bg: 'bg-orange-500/20', border: 'border-orange-400', text: 'text-orange-100',  dot: 'bg-orange-400' },
                amber:   { bg: 'bg-amber-500/25', border: 'border-amber-400',  text: 'text-amber-100',   dot: 'bg-amber-400' },
                lime:    { bg: 'bg-lime-500/20', border: 'border-lime-400',    text: 'text-lime-100',    dot: 'bg-lime-400' },
                emerald: { bg: 'bg-emerald-500/25', border: 'border-emerald-400', text: 'text-emerald-100', dot: 'bg-emerald-400' },
              };
              const unit = isBolt ? 's' : 'pas';
              const referenceLine = isBolt
                ? 'Cible clinique : ≥ 25 s pour une respiration normale. Objectif long terme : 40 s.'
                : 'Cible clinique : ≥ 60 pas. Objectif long terme : 100 pas et plus.';
              return (
                <div className="mb-6 text-left rounded-2xl p-4 sm:p-5 border border-white/20 bg-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white/80 text-xs uppercase tracking-widest font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {isBolt ? 'Interprétation BOLT' : 'Interprétation MBT'}
                    </p>
                    <span className="text-white/60 text-[10px]">Repères Buteyko</span>
                  </div>
                  <p className="text-white/70 text-xs mb-3 leading-relaxed">{referenceLine}</p>
                  <div className="space-y-1.5">
                    {rows.map((row) => {
                      const active = value >= row.min && value < row.max;
                      const palette = tonePalette[row.tone];
                      const rangeLabel = row.max === Infinity
                        ? `> ${row.min} ${unit}`
                        : `${row.min}-${row.max} ${unit}`;
                      return (
                        <div
                          key={row.status}
                          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border transition-all ${active ? `${palette.bg} ${palette.border} shadow-md scale-[1.02]` : 'bg-white/5 border-white/10'}`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${palette.dot}`} />
                          <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">
                            <span className={`text-xs font-semibold ${active ? palette.text : 'text-white/70'}`}>
                              {row.status}
                            </span>
                            <span className={`text-[11px] font-mono ${active ? palette.text : 'text-white/75'}`}>
                              {rangeLabel}
                            </span>
                          </div>
                          {active && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${palette.text} bg-white/15 border border-white/25 whitespace-nowrap`}>
                              Vous ici
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(() => {
                    const active = rows.find((r) => value >= r.min && value < r.max);
                    if (!active) return null;
                    return (
                      <p className="mt-3 text-xs text-white/80 leading-relaxed">
                        <span className="text-white font-semibold">{active.status} :</span> {active.hint}
                      </p>
                    );
                  })()}
                </div>
              );
            })()}

            {/* Optional contextual note - prominent amber panel */}
            {currentChallengeId && (
              <div
                className="mb-6 text-left rounded-2xl p-4 sm:p-5 border-2 shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.22) 0%, rgba(245, 158, 11, 0.14) 100%)',
                  borderColor: 'rgba(251, 191, 36, 0.65)',
                  boxShadow: '0 8px 24px -8px rgba(245, 158, 11, 0.35)'
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-amber-950 shadow-md">
                    <StickyNote size={16} strokeWidth={2.5} />
                  </div>
                  <label
                    className="block text-amber-100 text-base font-semibold tracking-wide"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Ajouter une note à ce score
                  </label>
                </div>
                <p className="text-white/85 text-sm mb-3 leading-relaxed">
                  État émotionnel, stress, fatigue, douleurs, contexte…&nbsp;
                  <span className="text-amber-200 font-medium">Ces éléments peuvent influencer votre score.</span>
                </p>
                <textarea
                  value={challengeNoteDraft}
                  onChange={(e) => { setChallengeNoteDraft(e.target.value.slice(0, 1000)); setChallengeNoteSaved(false); }}
                  placeholder="Ex : nuit courte, journée stressante au travail, rhume, douleur cervicale…"
                  rows={3}
                  maxLength={1000}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/95 border border-amber-200 text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none shadow-inner"
                  data-testid="challenge-note-textarea"
                />
                <div className="flex items-center justify-between mt-3 gap-3">
                  <span className="text-amber-100/80 text-xs">{challengeNoteDraft.length}/1000</span>
                  <button
                    type="button"
                    disabled={challengeNoteSaving || !currentChallengeId}
                    onClick={async () => {
                      if (!currentChallengeId) return;
                      setChallengeNoteSaving(true);
                      try {
                        await axios.patch(`${API}/challenges/${currentChallengeId}`, { notes: challengeNoteDraft });
                        setChallengeNoteSaved(true);
                        toast.success('Note enregistrée');
                      } catch (err) {
                        console.error('Failed to save challenge note', err);
                        toast.error("Impossible d'enregistrer la note");
                      } finally {
                        setChallengeNoteSaving(false);
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all shadow-md disabled:opacity-50 ${
                      challengeNoteSaved
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                        : 'bg-amber-400 hover:bg-amber-300 text-amber-950'
                    }`}
                    data-testid="challenge-note-save"
                  >
                    {challengeNoteSaving ? (
                      'Enregistrement…'
                    ) : challengeNoteSaved ? (
                      <>
                        <CheckCircle2 size={16} strokeWidth={2.5} />
                        Note enregistrée
                      </>
                    ) : (
                      <>
                        <StickyNote size={16} strokeWidth={2.5} />
                        Enregistrer la note
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Continue button */}
            <button
              onClick={() => { setShowRankingPopup(false); setCurrentChallengeScore(null); setCurrentChallengeId(null); setChallengeNoteDraft(''); setChallengeNoteSaved(false); }}
              className="px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              data-testid="ranking-popup-continue"
            >
              Continuer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreathingAppPage;
